import React, { useState, useEffect } from 'react';
import { useBusinessPlan } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { TooltipLabel } from './ui/TooltipLabel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import StrategicFrameworkLinker from './cost/StrategicFrameworkLinker';
import { financialPlanningTooltips } from '../utils/financialTooltips';
import { 
  Calendar,
  Target,
  Plus, 
  Edit3, 
  Trash2,
  TrendingUp,
  DollarSign,
  PieChart,
  Milestone,
  AlertCircle,
  CheckCircle,
  BarChart3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/financial-management`;
const PLANNING_API = `${BACKEND_URL}/api/financial-planning`;

const FinancialPlanningV2 = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  
  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [sfHierarchy, setSfHierarchy] = useState([]);
  const [loanTypes, setLoanTypes] = useState({});
  const [equityTypes, setEquityTypes] = useState({});
  
  // Planned Financing State
  const [plannedDebt, setPlannedDebt] = useState([]);
  const [plannedEquity, setPlannedEquity] = useState([]);
  const [milestones, setMilestones] = useState([]);
  
  // Dialogs
  const [showDebtDialog, setShowDebtDialog] = useState(false);
  const [showEquityDialog, setShowEquityDialog] = useState(false);
  const [showMilestoneDialog, setShowMilestoneDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dialogType, setDialogType] = useState('debt');
  
  // Forms
  const [debtForm, setDebtForm] = useState({
    financing_type: 'term_loan',
    target_amount: '',
    target_date: '',
    expected_interest_rate: '',
    linked_objective_id: '',
    linked_initiative_id: '',
    linked_revenue_stream_id: '',
    owner: '',
    notes: ''
  });

  const [equityForm, setEquityForm] = useState({
    equity_type: 'seed',
    target_amount: '',
    target_date: '',
    expected_valuation: '',
    expected_dilution: '',
    linked_objective_id: '',
    linked_initiative_id: '',
    linked_revenue_stream_id: '',
    owner: '',
    notes: ''
  });

  const [milestoneForm, setMilestoneForm] = useState({
    milestone_name: '',
    target_date: '',
    linked_objective_id: '',
    linked_initiative_id: '',
    linked_revenue_stream_id: '',
    linked_initiative_id: '',
    owner: '',
    metric: '',
    target_value: '',
    notes: ''
  });

  useEffect(() => {
    if (currentPlan?.id) {
      fetchAllData();
    }
  }, [currentPlan?.id]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      const [teamRes, sfRes, loanTypesRes, equityTypesRes, planningRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/enterprise/team-members?plan_id=${currentPlan.id}`),
        axios.get(`${BACKEND_URL}/api/v3/cost-management/sf-hierarchy/${currentPlan.id}`),
        axios.get(`${API}/loan-types`),
        axios.get(`${API}/equity-types`),
        axios.get(`${PLANNING_API}/complete/${currentPlan.id}`)
      ]);
      
      setTeamMembers(teamRes.data?.team_members || []);
      setSfHierarchy(sfRes.data?.hierarchy || []);
      setLoanTypes(loanTypesRes.data?.loan_types || {});
      setEquityTypes(equityTypesRes.data?.equity_types || {});
      
      // Load from backend API and map to frontend field names
      const planningData = planningRes.data;
      
      // Map planned loans from backend format to frontend format
      const mappedLoans = (planningData?.planned_loans || []).map(loan => ({
        ...loan,
        financing_type: loan.loan_type,
        target_amount: loan.planned_amount,
        target_date: loan.planned_start_date,
        notes: loan.purpose
      }));
      
      // Map planned equity from backend format to frontend format
      const mappedEquity = (planningData?.planned_equity_raises || []).map(equity => ({
        ...equity,
        equity_type: equity.target_investor_type || 'seed',
        target_amount: equity.planned_amount,
        target_date: equity.planned_raise_date,
        expected_valuation: equity.target_pre_money_valuation,
        expected_dilution: equity.expected_dilution_percent,
        notes: equity.use_of_funds
      }));
      
      // Map milestones from backend format to frontend format
      const mappedMilestones = (planningData?.financial_milestones || []).map(milestone => ({
        ...milestone,
        metric: milestone.milestone_type,
        notes: milestone.description
      }));
      
      setPlannedDebt(mappedLoans);
      setPlannedEquity(mappedEquity);
      setMilestones(mappedMilestones);
      
      console.log('✅ Financial planning data loaded from backend');
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load financial planning data');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlannedDebt = async (e) => {
    e.preventDefault();
    
    // Map frontend field names to backend model field names
    const debtData = {
      id: editingItem?.id || Date.now().toString(),
      plan_id: currentPlan.id,
      loan_name: `Planned ${debtForm.financing_type} Loan`,
      loan_type: debtForm.financing_type,
      planned_amount: parseFloat(debtForm.target_amount),
      expected_interest_rate: parseFloat(debtForm.expected_interest_rate),
      planned_term_months: 60, // Default 5 years, can be made configurable
      planned_start_date: debtForm.target_date,
      purpose: debtForm.notes || 'Strategic financing',
      linked_objective_id: debtForm.linked_objective_id || null,
      linked_initiative_id: debtForm.linked_initiative_id || null,
      created_at: editingItem?.created_at || new Date().toISOString()
    };

    try {
      if (editingItem) {
        // Update existing
        await axios.put(`${PLANNING_API}/loans/${editingItem.id}`, debtData);
        const updatedDebt = plannedDebt.map(d => d.id === editingItem.id ? debtData : d);
        setPlannedDebt(updatedDebt);
        toast.success('Planned debt updated!');
      } else {
        // Create new
        const response = await axios.post(`${PLANNING_API}/loans`, debtData);
        setPlannedDebt([...plannedDebt, response.data]);
        toast.success('Planned debt added!');
      }

      setShowDebtDialog(false);
      resetDebtForm();
    } catch (error) {
      console.error('Error saving planned debt:', error);
      toast.error('Failed to save planned debt');
    }
  };

  const handleDeletePlannedDebt = async (id) => {
    if (!window.confirm('Delete this planned debt?')) return;
    
    try {
      await axios.delete(`${PLANNING_API}/loans/${id}`);
      const updated = plannedDebt.filter(d => d.id !== id);
      setPlannedDebt(updated);
      toast.success('Planned debt deleted');
    } catch (error) {
      console.error('Error deleting planned debt:', error);
      toast.error('Failed to delete planned debt');
    }
  };

  const handleSavePlannedEquity = async (e) => {
    e.preventDefault();
    
    const plannedAmount = parseFloat(equityForm.target_amount);
    const preMoneyVal = parseFloat(equityForm.expected_valuation);
    const postMoneyVal = preMoneyVal + plannedAmount;
    const dilutionPercent = parseFloat(equityForm.expected_dilution) || ((plannedAmount / postMoneyVal) * 100);
    
    // Map frontend field names to backend model field names
    const equityData = {
      id: editingItem?.id || Date.now().toString(),
      plan_id: currentPlan.id,
      round_name: `${equityForm.equity_type.charAt(0).toUpperCase() + equityForm.equity_type.slice(1)} Round`,
      planned_amount: plannedAmount,
      target_pre_money_valuation: preMoneyVal,
      target_post_money_valuation: postMoneyVal,
      expected_dilution_percent: dilutionPercent,
      planned_shares_to_issue: 10000, // Default, can be made configurable
      target_price_per_share: plannedAmount / 10000,
      planned_raise_date: equityForm.target_date,
      use_of_funds: equityForm.notes || 'Strategic growth',
      minimum_acceptable_amount: plannedAmount * 0.8, // 80% of target
      maximum_dilution_acceptable: dilutionPercent * 1.2, // 20% more than expected
      target_investor_type: equityForm.equity_type === 'seed' ? 'angel' : equityForm.equity_type === 'series_a' ? 'vc' : 'strategic',
      linked_objective_id: equityForm.linked_objective_id || null,
      linked_initiative_id: equityForm.linked_initiative_id || null,
      created_at: editingItem?.created_at || new Date().toISOString()
    };

    try {
      if (editingItem) {
        // Update existing
        await axios.put(`${PLANNING_API}/equity-raises/${editingItem.id}`, equityData);
        const updatedEquity = plannedEquity.map(e => e.id === editingItem.id ? equityData : e);
        setPlannedEquity(updatedEquity);
        toast.success('Planned equity updated!');
      } else {
        // Create new
        const response = await axios.post(`${PLANNING_API}/equity-raises`, equityData);
        setPlannedEquity([...plannedEquity, response.data]);
        toast.success('Planned equity added!');
      }

      setShowEquityDialog(false);
      resetEquityForm();
    } catch (error) {
      console.error('Error saving planned equity:', error);
      toast.error('Failed to save planned equity');
    }
  };

  const handleDeletePlannedEquity = async (id) => {
    if (!window.confirm('Delete this planned equity?')) return;
    
    try {
      await axios.delete(`${PLANNING_API}/equity-raises/${id}`);
      const updated = plannedEquity.filter(e => e.id !== id);
      setPlannedEquity(updated);
      toast.success('Planned equity deleted');
    } catch (error) {
      console.error('Error deleting planned equity:', error);
      toast.error('Failed to delete planned equity');
    }
  };

  const handleSaveMilestone = async (e) => {
    e.preventDefault();
    
    // Map frontend field names to backend model field names
    const milestoneData = {
      id: editingItem?.id || Date.now().toString(),
      plan_id: currentPlan.id,
      milestone_name: milestoneForm.milestone_name,
      milestone_type: milestoneForm.metric || 'revenue', // Default to revenue
      target_date: milestoneForm.target_date,
      target_value: parseFloat(milestoneForm.target_value) || 0,
      description: milestoneForm.notes || 'Financial milestone',
      success_criteria: `Achieve ${milestoneForm.metric || 'target'} of ${milestoneForm.target_value}`,
      linked_objective_id: milestoneForm.linked_objective_id || null,
      linked_initiative_id: milestoneForm.linked_initiative_id || null,
      created_at: editingItem?.created_at || new Date().toISOString()
    };

    try {
      if (editingItem) {
        // Update existing
        await axios.put(`${PLANNING_API}/milestones/${editingItem.id}`, milestoneData);
        const updatedMilestones = milestones.map(m => m.id === editingItem.id ? milestoneData : m);
        setMilestones(updatedMilestones);
        toast.success('Milestone updated!');
      } else {
        // Create new
        const response = await axios.post(`${PLANNING_API}/milestones`, milestoneData);
        setMilestones([...milestones, response.data]);
        toast.success('Milestone created!');
      }

      setShowMilestoneDialog(false);
      resetMilestoneForm();
    } catch (error) {
      console.error('Error saving milestone:', error);
      toast.error('Failed to save milestone');
    }
  };

  const handleDeleteMilestone = async (id) => {
    if (!window.confirm('Delete this milestone?')) return;
    
    try {
      await axios.delete(`${PLANNING_API}/milestones/${id}`);
      const updated = milestones.filter(m => m.id !== id);
      setMilestones(updated);
      toast.success('Milestone deleted');
    } catch (error) {
      console.error('Error deleting milestone:', error);
      toast.error('Failed to delete milestone');
    }
  };

  const resetDebtForm = () => {
    setDebtForm({
      financing_type: 'term_loan',
      target_amount: '',
      target_date: '',
      expected_interest_rate: '',
      linked_objective_id: '',
      linked_initiative_id: '',
      linked_revenue_stream_id: '',
      owner: '',
      notes: ''
    });
  };

  const resetEquityForm = () => {
    setEquityForm({
      equity_type: 'seed',
      target_amount: '',
      target_date: '',
      expected_valuation: '',
      expected_dilution: '',
      linked_objective_id: '',
      linked_initiative_id: '',
      linked_revenue_stream_id: '',
      owner: '',
      notes: ''
    });
  };

  const resetMilestoneForm = () => {
    setMilestoneForm({
      milestone_name: '',
      target_date: '',
      linked_objective_id: '',
      linked_initiative_id: '',
      linked_revenue_stream_id: '',
      owner: '',
      metric: '',
      target_value: '',
      notes: ''
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return formatCurrencyContext(0, currentPlan?.currency || 'USD');
    return formatCurrencyContext(amount, currentPlan?.currency || 'USD', null, false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading financial planning...</p>
        </div>
      </div>
    );
  }

  const totalPlannedDebt = plannedDebt.reduce((sum, d) => sum + (d.target_amount || 0), 0);
  const totalPlannedEquity = plannedEquity.reduce((sum, e) => sum + (e.target_amount || 0), 0);
  const totalPlannedCapital = totalPlannedDebt + totalPlannedEquity;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Financial Planning</h1>
          <p className="text-gray-600 mt-1">
            Plan future financing, track milestones, and analyze funding requirements
          </p>
        </div>
        <Badge variant="outline" className="text-sm">
          {currentPlan.company_name}
        </Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Planned Capital</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalPlannedCapital)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Planned Debt</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalPlannedDebt)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Planned Equity</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalPlannedEquity)}</p>
              </div>
              <PieChart className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Milestones</p>
                <p className="text-2xl font-bold text-gray-900">{milestones.length}</p>
              </div>
              <Milestone className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="planned-debt">
            <TrendingUp className="h-4 w-4 mr-2" />
            Planned Debt
          </TabsTrigger>
          <TabsTrigger value="planned-equity">
            <PieChart className="h-4 w-4 mr-2" />
            Planned Equity
          </TabsTrigger>
          <TabsTrigger value="milestones">
            <Milestone className="h-4 w-4 mr-2" />
            Milestones
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Overview */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Financing Timeline</CardTitle>
              <CardDescription>Planned capital raises and key milestones over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[...plannedDebt, ...plannedEquity, ...milestones].length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                    <p>No financing events or milestones planned</p>
                    <p className="text-sm">Add planned debt, equity, or milestones to see timeline</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Timeline items sorted by date */}
                    {[...plannedDebt.map(d => ({...d, type: 'debt'})), 
                      ...plannedEquity.map(e => ({...e, type: 'equity'})),
                      ...milestones.map(m => ({...m, type: 'milestone'}))]
                      .sort((a, b) => new Date(a.target_date) - new Date(b.target_date))
                      .map((item, index) => (
                        <div key={index} className="flex items-center space-x-4 p-3 border rounded hover:bg-gray-50">
                          <div className="flex-shrink-0">
                            {item.type === 'debt' && <TrendingUp className="h-6 w-6 text-blue-600" />}
                            {item.type === 'equity' && <PieChart className="h-6 w-6 text-purple-600" />}
                            {item.type === 'milestone' && <Milestone className="h-6 w-6 text-orange-600" />}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">
                              {item.milestone_name || item.financing_type || item.equity_type}
                            </p>
                            <p className="text-sm text-gray-600">
                              {item.type === 'milestone' ? item.metric : formatCurrency(item.target_amount)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              {new Date(item.target_date).toLocaleDateString()}
                            </p>
                            <Badge variant="outline" className="text-xs mt-1">{item.type}</Badge>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 2: Planned Debt */}
        <TabsContent value="planned-debt" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Planned Debt Financing</CardTitle>
                  <CardDescription>Future debt you plan to raise</CardDescription>
                </div>
                <Button onClick={() => {
                  resetDebtForm();
                  setEditingItem(null);
                  setShowDebtDialog(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Planned Debt
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {plannedDebt.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <TrendingUp className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p>No planned debt financing</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interest</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Initiative</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {plannedDebt.map((debt) => (
                        <tr key={debt.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">{debt.financing_type}</p>
                            {debt.owner && <p className="text-xs text-gray-500">{debt.owner}</p>}
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900">{formatCurrency(debt.target_amount)}</td>
                          <td className="px-4 py-3 text-gray-600">{new Date(debt.target_date).toLocaleDateString()}</td>
                          <td className="px-4 py-3">{debt.expected_interest_rate}%</td>
                          <td className="px-4 py-3">
                            {debt.linked_initiative_id ? (
                              <Badge variant="secondary" className="text-xs">Linked</Badge>
                            ) : (
                              <span className="text-xs text-gray-400">Not linked</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end space-x-2">
                              <Button variant="ghost" size="sm" onClick={() => {
                                setEditingItem(debt);
                                // Properly populate form with debt fields
                                setDebtForm({
                                  financing_type: debt.financing_type || debt.loan_type || 'term_loan',
                                  target_amount: debt.target_amount || debt.planned_amount || '',
                                  target_date: debt.target_date || debt.planned_start_date || '',
                                  expected_interest_rate: debt.expected_interest_rate || '',
                                  linked_objective_id: debt.linked_objective_id || '',
                                  linked_initiative_id: debt.linked_initiative_id || '',
                                  linked_revenue_stream_id: debt.linked_revenue_stream_id || '',
                                  owner: debt.owner || '',
                                  notes: debt.notes || debt.purpose || ''
                                });
                                setShowDebtDialog(true);
                              }}>
                                <Edit3 className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeletePlannedDebt(debt.id)} className="text-red-600">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 3: Planned Equity */}
        <TabsContent value="planned-equity" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Planned Equity Financing</CardTitle>
                  <CardDescription>Future equity raises you plan to execute</CardDescription>
                </div>
                <Button onClick={() => {
                  resetEquityForm();
                  setEditingItem(null);
                  setShowEquityDialog(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Planned Equity
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {plannedEquity.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <PieChart className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p>No planned equity financing</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valuation</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dilution</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {plannedEquity.map((equity) => (
                        <tr key={equity.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">{equityTypes[equity.equity_type] || equity.equity_type}</p>
                            {equity.owner && <p className="text-xs text-gray-500">{equity.owner}</p>}
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900">{formatCurrency(equity.target_amount)}</td>
                          <td className="px-4 py-3 text-gray-600">{new Date(equity.target_date).toLocaleDateString()}</td>
                          <td className="px-4 py-3">{formatCurrency(equity.expected_valuation)}</td>
                          <td className="px-4 py-3">{equity.expected_dilution}%</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end space-x-2">
                              <Button variant="ghost" size="sm" onClick={() => {
                                setEditingItem(equity);
                                // Properly populate form with equity fields
                                setEquityForm({
                                  equity_type: equity.equity_type || equity.target_investor_type || 'seed',
                                  target_amount: equity.target_amount || equity.planned_amount || '',
                                  target_date: equity.target_date || equity.planned_raise_date || '',
                                  expected_valuation: equity.expected_valuation || equity.target_pre_money_valuation || '',
                                  expected_dilution: equity.expected_dilution || equity.expected_dilution_percent || '',
                                  linked_objective_id: equity.linked_objective_id || '',
                                  linked_initiative_id: equity.linked_initiative_id || '',
                                  linked_revenue_stream_id: equity.linked_revenue_stream_id || '',
                                  owner: equity.owner || '',
                                  notes: equity.notes || equity.use_of_funds || ''
                                });
                                setShowEquityDialog(true);
                              }}>
                                <Edit3 className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeletePlannedEquity(equity.id)} className="text-red-600">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 4: Milestones */}
        <TabsContent value="milestones" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Strategic Milestones</CardTitle>
                  <CardDescription>Key achievements and targets to track</CardDescription>
                </div>
                <Button onClick={() => {
                  resetMilestoneForm();
                  setEditingItem(null);
                  setShowMilestoneDialog(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Milestone
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {milestones.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Milestone className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p>No milestones defined</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {milestones.map((milestone) => (
                    <div key={milestone.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <Milestone className="h-5 w-5 text-orange-600" />
                            <h4 className="font-medium text-gray-900">{milestone.milestone_name}</h4>
                            {milestone.linked_initiative_id && (
                              <Badge variant="secondary" className="text-xs">
                                <Target className="h-3 w-3 mr-1" />
                                Linked to Initiative
                              </Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-600">Target:</span>
                              <span className="ml-2 font-medium">{milestone.metric}: {milestone.target_value}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Date:</span>
                              <span className="ml-2 font-medium">{new Date(milestone.target_date).toLocaleDateString()}</span>
                            </div>
                            {milestone.owner && (
                              <div>
                                <span className="text-gray-600">Owner:</span>
                                <span className="ml-2 font-medium">{milestone.owner}</span>
                              </div>
                            )}
                          </div>
                          {milestone.notes && (
                            <p className="text-sm text-gray-600 mt-2">{milestone.notes}</p>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <Button variant="ghost" size="sm" onClick={() => {
                            setEditingItem(milestone);
                            // Properly populate form with milestone fields
                            setMilestoneForm({
                              milestone_name: milestone.milestone_name || '',
                              target_date: milestone.target_date || '',
                              linked_objective_id: milestone.linked_objective_id || '',
                              linked_initiative_id: milestone.linked_initiative_id || '',
                              linked_revenue_stream_id: milestone.linked_revenue_stream_id || '',
                              owner: milestone.owner || '',
                              metric: milestone.metric || milestone.milestone_type || '',
                              target_value: milestone.target_value || '',
                              notes: milestone.notes || milestone.description || ''
                            });
                            setShowMilestoneDialog(true);
                          }}>
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteMilestone(milestone.id)} className="text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Planned Debt Dialog */}
      <Dialog open={showDebtDialog} onOpenChange={setShowDebtDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit' : 'Add'} Planned Debt</DialogTitle>
            <DialogDescription>Plan future debt financing to support strategic initiatives</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSavePlannedDebt} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <TooltipLabel htmlFor="debt-type" tooltip={financialPlanningTooltips.financingType}>
                  Debt Type *
                </TooltipLabel>
                <Select value={debtForm.financing_type} onValueChange={(val) => setDebtForm({...debtForm, financing_type: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(loanTypes).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        {value.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <TooltipLabel htmlFor="debt-amount" tooltip={financialPlanningTooltips.targetAmount}>
                  Target Amount ($) *
                </TooltipLabel>
                <Input
                  id="debt-amount"
                  type="number"
                  value={debtForm.target_amount}
                  onChange={(e) => setDebtForm({...debtForm, target_amount: e.target.value})}
                  placeholder="e.g., 500000"
                  required
                />
              </div>

              <div>
                <TooltipLabel htmlFor="debt-date" tooltip={financialPlanningTooltips.targetDate}>
                  Target Date *
                </TooltipLabel>
                <Input
                  id="debt-date"
                  type="date"
                  value={debtForm.target_date}
                  onChange={(e) => setDebtForm({...debtForm, target_date: e.target.value})}
                  required
                />
              </div>

              <div>
                <TooltipLabel htmlFor="debt-interest" tooltip={financialPlanningTooltips.expectedInterestRate}>
                  Expected Interest Rate (%)
                </TooltipLabel>
                <Input
                  id="debt-interest"
                  type="number"
                  step="0.1"
                  value={debtForm.expected_interest_rate}
                  onChange={(e) => setDebtForm({...debtForm, expected_interest_rate: e.target.value})}
                  placeholder="e.g., 8.5"
                />
              </div>
            </div>

            {/* Strategic Framework Linking - Full Cascading */}
            <StrategicFrameworkLinker 
              form={debtForm}
              setForm={setDebtForm}
              sfHierarchy={sfHierarchy}
            />

            <div>
              <TooltipLabel htmlFor="debt-owner" tooltip="Person managing this financing">
                Owner
              </TooltipLabel>
              <Select value={debtForm.owner || '__none__'} onValueChange={(val) => setDebtForm({...debtForm, owner: val === '__none__' ? '' : val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No Assignment</SelectItem>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.name}>
                      {member.name} {member.role && `(${member.role})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <TooltipLabel htmlFor="debt-notes" tooltip="Additional details">
                Notes
              </TooltipLabel>
              <Textarea
                id="debt-notes"
                value={debtForm.notes}
                onChange={(e) => setDebtForm({...debtForm, notes: e.target.value})}
                placeholder="Purpose, requirements, etc."
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDebtDialog(false)}>Cancel</Button>
              <Button type="submit">{editingItem ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Planned Equity Dialog */}
      <Dialog open={showEquityDialog} onOpenChange={setShowEquityDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit' : 'Add'} Planned Equity</DialogTitle>
            <DialogDescription>Plan future equity financing rounds</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSavePlannedEquity} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <TooltipLabel htmlFor="equity-type" tooltip={financialPlanningTooltips.financingType}>
                  Equity Type *
                </TooltipLabel>
                <Select value={equityForm.equity_type} onValueChange={(val) => setEquityForm({...equityForm, equity_type: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(equityTypes).map(([key, value]) => (
                      <SelectItem key={key} value={key}>{value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <TooltipLabel htmlFor="equity-amount" tooltip={financialPlanningTooltips.targetAmount}>
                  Target Amount ($) *
                </TooltipLabel>
                <Input
                  id="equity-amount"
                  type="number"
                  value={equityForm.target_amount}
                  onChange={(e) => setEquityForm({...equityForm, target_amount: e.target.value})}
                  placeholder="e.g., 2000000"
                  required
                />
              </div>

              <div>
                <TooltipLabel htmlFor="equity-date" tooltip={financialPlanningTooltips.targetDate}>
                  Target Date *
                </TooltipLabel>
                <Input
                  id="equity-date"
                  type="date"
                  value={equityForm.target_date}
                  onChange={(e) => setEquityForm({...equityForm, target_date: e.target.value})}
                  required
                />
              </div>

              <div>
                <TooltipLabel htmlFor="valuation" tooltip={financialPlanningTooltips.expectedValuation}>
                  Expected Valuation ($)
                </TooltipLabel>
                <Input
                  id="valuation"
                  type="number"
                  value={equityForm.expected_valuation}
                  onChange={(e) => setEquityForm({...equityForm, expected_valuation: e.target.value})}
                  placeholder="e.g., 10000000"
                />
              </div>

              <div>
                <TooltipLabel htmlFor="dilution" tooltip={financialPlanningTooltips.expectedDilution}>
                  Expected Dilution (%)
                </TooltipLabel>
                <Input
                  id="dilution"
                  type="number"
                  step="0.1"
                  value={equityForm.expected_dilution}
                  onChange={(e) => setEquityForm({...equityForm, expected_dilution: e.target.value})}
                  placeholder="e.g., 20"
                />
              </div>
            </div>

            {/* Strategic Framework Linking - Full Cascading */}
            <StrategicFrameworkLinker 
              form={equityForm}
              setForm={setEquityForm}
              sfHierarchy={sfHierarchy}
            />

            <div>
              <TooltipLabel htmlFor="equity-owner" tooltip="Person managing this raise">
                Owner
              </TooltipLabel>
              <Select value={equityForm.owner || '__none__'} onValueChange={(val) => setEquityForm({...equityForm, owner: val === '__none__' ? '' : val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No Assignment</SelectItem>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.name}>
                      {member.name} {member.role && `(${member.role})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <TooltipLabel htmlFor="equity-notes" tooltip="Additional details">
                Notes
              </TooltipLabel>
              <Textarea
                id="equity-notes"
                value={equityForm.notes}
                onChange={(e) => setEquityForm({...equityForm, notes: e.target.value})}
                placeholder="Investor details, terms, etc."
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEquityDialog(false)}>Cancel</Button>
              <Button type="submit">{editingItem ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Milestone Dialog */}
      <Dialog open={showMilestoneDialog} onOpenChange={setShowMilestoneDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit' : 'Add'} Milestone</DialogTitle>
            <DialogDescription>Track key achievements and targets</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveMilestone} className="space-y-4">
            <div>
              <TooltipLabel htmlFor="milestone-name" tooltip={financialPlanningTooltips.milestoneName}>
                Milestone Name *
              </TooltipLabel>
              <Input
                id="milestone-name"
                value={milestoneForm.milestone_name}
                onChange={(e) => setMilestoneForm({...milestoneForm, milestone_name: e.target.value})}
                placeholder="e.g., Reach 10,000 Users"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <TooltipLabel htmlFor="milestone-metric" tooltip={financialPlanningTooltips.milestoneMetric}>
                  Success Metric *
                </TooltipLabel>
                <Input
                  id="milestone-metric"
                  value={milestoneForm.metric}
                  onChange={(e) => setMilestoneForm({...milestoneForm, metric: e.target.value})}
                  placeholder="e.g., Active Users, Revenue, Market Share"
                  required
                />
              </div>

              <div>
                <TooltipLabel htmlFor="milestone-value" tooltip="Target value for the metric">
                  Target Value *
                </TooltipLabel>
                <Input
                  id="milestone-value"
                  value={milestoneForm.target_value}
                  onChange={(e) => setMilestoneForm({...milestoneForm, target_value: e.target.value})}
                  placeholder="e.g., 10000"
                  required
                />
              </div>

              <div>
                <TooltipLabel htmlFor="milestone-date" tooltip={financialPlanningTooltips.milestoneDate}>
                  Target Date *
                </TooltipLabel>
                <Input
                  id="milestone-date"
                  type="date"
                  value={milestoneForm.target_date}
                  onChange={(e) => setMilestoneForm({...milestoneForm, target_date: e.target.value})}
                  required
                />
              </div>

              <div>
                <TooltipLabel htmlFor="milestone-owner" tooltip={financialPlanningTooltips.milestoneOwner}>
                  Milestone Owner
                </TooltipLabel>
                <Select value={milestoneForm.owner || '__none__'} onValueChange={(val) => setMilestoneForm({...milestoneForm, owner: val === '__none__' ? '' : val})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select owner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No Assignment</SelectItem>
                    {teamMembers.map(member => (
                      <SelectItem key={member.id} value={member.name}>
                        {member.name} {member.role && `(${member.role})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Strategic Framework Linking - Full Cascading */}
            <StrategicFrameworkLinker 
              form={milestoneForm}
              setForm={setMilestoneForm}
              sfHierarchy={sfHierarchy}
            />

            <div>
              <TooltipLabel htmlFor="milestone-notes" tooltip="Additional context">
                Notes
              </TooltipLabel>
              <Textarea
                id="milestone-notes"
                value={milestoneForm.notes}
                onChange={(e) => setMilestoneForm({...milestoneForm, notes: e.target.value})}
                placeholder="How to achieve, dependencies, etc."
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowMilestoneDialog(false)}>Cancel</Button>
              <Button type="submit">{editingItem ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancialPlanningV2;
